import { Tool } from '@modelcontextprotocol/sdk/types.js';
import { z } from 'zod';
import type { Config } from '../types/index.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { findFiles } from '../utils/fs.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const suggestMigrationTool: Tool = {
  name: 'suggest_migration',
  description: 'Suggest a migration name following SmartStack conventions ({context}_v{version}_{sequence}_{Description})',
  inputSchema: {
    type: 'object',
    properties: {
      description: {
        type: 'string',
        description: 'Description of what the migration does (e.g., "Add User Profiles", "Create Orders Table")',
      },
      context: {
        type: 'string',
        enum: ['core', 'extensions'],
        description: 'DbContext name (default: auto-detected from .smartstack/config.json, or "core" for platform projects)',
      },
      version: {
        type: 'string',
        description: 'Semver version (e.g., "1.0.0", "1.2.0"). If not provided, uses latest from existing migrations.',
      },
    },
    required: ['description'],
  },
};

export const SuggestMigrationInputSchema = z.object({
  description: z.string().describe('Description of what the migration does'),
  context: z.enum(['core', 'extensions']).optional().describe('DbContext name (default: auto-detected from project config)'),
  version: z.string().optional().describe('Semver version (e.g., "1.0.0")'),
});

export type SuggestMigrationInput = z.infer<typeof SuggestMigrationInputSchema>;

interface MigrationInfo {
  name: string;
  context: string;
  version: string;
  sequence: number;
  description: string;
}

export async function handleSuggestMigration(
  args: unknown,
  config: Config
): Promise<string> {
  const input = SuggestMigrationInputSchema.parse(args);
  // Use project's default DbContext if not explicitly specified, fallback to 'core'
  const context = input.context || config.defaultDbContext || 'core';

  logger.info('Suggesting migration name', { description: input.description, context });

  const structure = await findSmartStackStructure(config.smartstack.projectPath);

  // Find existing migrations
  const existingMigrations = await findExistingMigrations(structure, config, context);

  // Determine version and sequence
  let version = input.version;
  let sequence = 1;

  if (existingMigrations.length > 0) {
    // Get latest migration for this context
    const latestMigration = existingMigrations[existingMigrations.length - 1];

    if (!version) {
      // Use the same version as the latest migration
      version = latestMigration.version;
    }

    // Calculate next sequence number for this version
    const migrationsInVersion = existingMigrations.filter(m => m.version === version);
    if (migrationsInVersion.length > 0) {
      const maxSequence = Math.max(...migrationsInVersion.map(m => m.sequence));
      sequence = maxSequence + 1;
    }
  } else {
    // No existing migrations, start fresh
    version = version || '1.0.0';
  }

  // Format description to PascalCase
  const pascalDescription = toPascalCase(input.description);

  // Format sequence with leading zeros
  const sequenceStr = sequence.toString().padStart(3, '0');

  // Build migration name following convention
  const migrationName = `${context}_v${version}_${sequenceStr}_${pascalDescription}`;

  // Generate the full command with the appropriate DbContext
  // CoreDbContext for SmartStack core entities, ExtensionsDbContext for client entities
  const dbContextName = context === 'core' ? 'CoreDbContext' : 'ExtensionsDbContext';
  const outputPath = context === 'extensions' ? 'Persistence/Migrations/Extensions' : 'Persistence/Migrations';
  const command = `dotnet ef migrations add ${migrationName} --context ${dbContextName} --project ../SmartStack.Infrastructure -o ${outputPath}`;

  // Build response
  const lines: string[] = [];
  lines.push('# Migration Name Suggestion');
  lines.push('');
  lines.push('## Suggested Name');
  lines.push('```');
  lines.push(migrationName);
  lines.push('```');
  lines.push('');
  lines.push('## Command');
  lines.push('```bash');
  lines.push(command);
  lines.push('```');
  lines.push('');
  lines.push('## Convention Details');
  lines.push('');
  lines.push(`| Part | Value | Description |`);
  lines.push(`|------|-------|-------------|`);
  lines.push(`| Context | \`${context}\` | Migration prefix (core/extensions) |`);
  lines.push(`| DbContext | \`${dbContextName}\` | EF Core DbContext to use |`);
  lines.push(`| Schema | \`${context}\` | Database schema for tables |`);
  lines.push(`| Version | \`v${version}\` | Semver version |`);
  lines.push(`| Sequence | \`${sequenceStr}\` | Order in version |`);
  lines.push(`| Description | \`${pascalDescription}\` | Migration description |`);
  lines.push('');
  lines.push('> **Note**: Migrations are stored in separate history tables:');
  lines.push(`> - Core: \`core.__EFMigrationsHistory\``);
  lines.push(`> - Extensions: \`extensions.__EFMigrationsHistory\``);
  lines.push('');

  if (existingMigrations.length > 0) {
    lines.push('## Existing Migrations (last 5)');
    lines.push('');
    const recent = existingMigrations.slice(-5);
    for (const m of recent) {
      lines.push(`- \`${m.name}\``);
    }
  }

  return lines.join('\n');
}

async function findExistingMigrations(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  context: string
): Promise<MigrationInfo[]> {
  const migrations: MigrationInfo[] = [];

  // Look for migrations in Infrastructure project
  const infraPath = structure.infrastructure || path.join(config.smartstack.projectPath, 'Infrastructure');
  const migrationsPath = path.join(infraPath, 'Persistence', 'Migrations');

  try {
    const migrationFiles = await findFiles('*.cs', { cwd: migrationsPath });

    // Pattern: {context}_v{version}_{sequence}_{Description}.cs
    // Also matches Designer.cs and Snapshot.cs which we want to skip
    const migrationPattern = /^(\w+)_v(\d+\.\d+\.\d+)_(\d+)_(\w+)\.cs$/;

    for (const file of migrationFiles) {
      const fileName = path.basename(file);

      // Skip Designer and Snapshot files
      if (fileName.includes('.Designer.') || fileName.includes('ModelSnapshot')) {
        continue;
      }

      const match = fileName.match(migrationPattern);
      if (match) {
        const [, ctx, ver, seq, desc] = match;
        if (ctx === context || !context) {
          migrations.push({
            name: fileName.replace('.cs', ''),
            context: ctx,
            version: ver,
            sequence: parseInt(seq, 10),
            description: desc,
          });
        }
      }
    }

    // Sort by version then sequence
    migrations.sort((a, b) => {
      const verCompare = compareVersions(a.version, b.version);
      if (verCompare !== 0) return verCompare;
      return a.sequence - b.sequence;
    });
  } catch {
    // Migrations folder doesn't exist yet
    logger.debug('No migrations folder found');
  }

  return migrations;
}

function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
    .split(/\s+/)                    // Split on whitespace
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

function compareVersions(a: string, b: string): number {
  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);

  for (let i = 0; i < 3; i++) {
    if (aParts[i] > bParts[i]) return 1;
    if (aParts[i] < bParts[i]) return -1;
  }

  return 0;
}
